import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import axios from 'axios';

const ContractorPageManage = () => {
  const navigate = useNavigate();

  const [activeLanguage, setActiveLanguage] = useState('en');
  const [contentId, setContentId] = useState(null);
  const [isNewContent, setIsNewContent] = useState(false);
  const [formData, setFormData] = useState({
    hero: {
      title: '',
      subtitle: '',
      paragraphs: [''],
    },
    statistics: {
      ledStrips: {
        number: '',
        description: '',
      },
      retailSpace: {
        number: '',
        description: '',
      },
    },
  });

  const [heroFile, setHeroFile] = useState(null);
  const [spanishData, setSpanishData] = useState({
    hero: {
      title: '',
      subtitle: '',
      paragraphs: [''],
    },
    statistics: {
      ledStrips: {
        number: '',
        description: '',
      },
      retailSpace: {
        number: '',
        description: '',
      },
    },
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [existingHeroFileUrl, setExistingHeroFileUrl] = useState('');

  useEffect(() => {
    const fetchContractorData = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/api/admin/contractor?includeSpanish=true`, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.data && response.data.data && response.data.data.length > 0) {
          const data = response.data.data[0];
          setContentId(data._id);
          setIsNewContent(false);

          setFormData({
            hero: {
              title: data.hero?.title || '',
              subtitle: data.hero?.subtitle || '',
              paragraphs: data.hero?.paragraphs?.length > 0 ? data.hero.paragraphs : [''],
            },
            statistics: {
              ledStrips: {
                number: data.statistics?.ledStrips?.number || '',
                description: data.statistics?.ledStrips?.description || '',
              },
              retailSpace: {
                number: data.statistics?.retailSpace?.number || '',
                description: data.statistics?.retailSpace?.description || '',
              },
            },
          });
          setExistingHeroFileUrl(data.hero?.fileUrl || '');

          setSpanishData({
            hero: {
              title: data.spanishData?.hero?.title || '',
              subtitle: data.spanishData?.hero?.subtitle || '',
              paragraphs: data.spanishData?.hero?.paragraphs?.length > 0 ? data.spanishData.hero.paragraphs : [''],
            },
            statistics: {
              ledStrips: {
                number: data.spanishData?.statistics?.ledStrips?.number || '',
                description: data.spanishData?.statistics?.ledStrips?.description || '',
              },
              retailSpace: {
                number: data.spanishData?.statistics?.retailSpace?.number || '',
                description: data.spanishData?.statistics?.retailSpace?.description || '',
              },
            },
          });
        } else {
          setIsNewContent(true);
        }
      } catch (err) {
        console.error('Error fetching contractor data:', err);
        if (err.response?.status === 404 || err.response?.status === 400) {
          setIsNewContent(true);
        } else if (err.response?.status === 401) {
          setErrors({ submit: 'Authentication failed. Please login again.' });
          setTimeout(() => navigate('/admin/login'), 2000);
        } else {
          setErrors({ submit: 'Failed to fetch contractor page details' });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContractorData();
  }, [navigate]);

  // English Handlers
  const handleHeroChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value },
    }));
  };

  const handleHeroParagraphChange = (index, value) => {
    const newParagraphs = [...formData.hero.paragraphs];
    newParagraphs[index] = value;
    setFormData(prev => ({
      ...prev,
      hero: { ...prev.hero, paragraphs: newParagraphs },
    }));
  };

  const addHeroParagraph = () => {
    setFormData(prev => ({
      ...prev,
      hero: { ...prev.hero, paragraphs: [...prev.hero.paragraphs, ''] },
    }));
  };

  const removeHeroParagraph = (index) => {
    if (formData.hero.paragraphs.length > 1) {
      const newParagraphs = formData.hero.paragraphs.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        hero: { ...prev.hero, paragraphs: newParagraphs },
      }));
    }
  };

  const handleStatisticsChange = (category, field, value) => {
    setFormData(prev => ({
      ...prev,
      statistics: {
        ...prev.statistics,
        [category]: {
          ...prev.statistics[category],
          [field]: value,
        },
      },
    }));
  };

  // Spanish Handlers
  const handleSpanishHeroChange = (field, value) => {
    setSpanishData(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value },
    }));
  };

  const handleSpanishHeroParagraphChange = (index, value) => {
    const newParagraphs = [...spanishData.hero.paragraphs];
    newParagraphs[index] = value;
    setSpanishData(prev => ({
      ...prev,
      hero: { ...prev.hero, paragraphs: newParagraphs },
    }));
  };

  const addSpanishHeroParagraph = () => {
    setSpanishData(prev => ({
      ...prev,
      hero: { ...prev.hero, paragraphs: [...prev.hero.paragraphs, ''] },
    }));
  };

  const removeSpanishHeroParagraph = (index) => {
    if (spanishData.hero.paragraphs.length > 1) {
      const newParagraphs = spanishData.hero.paragraphs.filter((_, i) => i !== index);
      setSpanishData(prev => ({
        ...prev,
        hero: { ...prev.hero, paragraphs: newParagraphs },
      }));
    }
  };

  const handleSpanishStatisticsChange = (category, field, value) => {
    setSpanishData(prev => ({
      ...prev,
      statistics: {
        ...prev.statistics,
        [category]: {
          ...prev.statistics[category],
          [field]: value,
        },
      },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.hero.title.trim()) newErrors['hero.title'] = 'Hero title is required';
    if (!formData.hero.subtitle.trim()) newErrors['hero.subtitle'] = 'Hero subtitle is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const fd = new FormData();

      fd.append('hero', JSON.stringify({
        title: formData.hero.title.trim(),
        subtitle: formData.hero.subtitle.trim(),
        paragraphs: formData.hero.paragraphs.filter(p => p.trim()).map(p => p.trim()),
      }));

      fd.append('statistics', JSON.stringify({
        ledStrips: {
          number: formData.statistics.ledStrips.number.trim(),
          description: formData.statistics.ledStrips.description.trim(),
        },
        retailSpace: {
          number: formData.statistics.retailSpace.number.trim(),
          description: formData.statistics.retailSpace.description.trim(),
        },
      }));

      fd.append('spanishData', JSON.stringify({
        hero: {
          title: spanishData.hero.title.trim(),
          subtitle: spanishData.hero.subtitle.trim(),
          paragraphs: spanishData.hero.paragraphs.filter(p => p.trim()).map(p => p.trim()),
        },
        statistics: {
          ledStrips: {
            number: spanishData.statistics.ledStrips.number.trim(),
            description: spanishData.statistics.ledStrips.description.trim(),
          },
          retailSpace: {
            number: spanishData.statistics.retailSpace.number.trim(),
            description: spanishData.statistics.retailSpace.description.trim(),
          },
        },
      }));

      if (heroFile) fd.append('heroFile', heroFile);

      let response;

      if (isNewContent) {
        response = await axios.post(`${config.BASE_URL}/api/admin/contractor`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.status === 201) {
          setContentId(response.data.data._id);
          setIsNewContent(false);
          setSuccessMessage('Contractor content created successfully!');
          const saved = response.data.data;
          setExistingHeroFileUrl(saved?.hero?.fileUrl || '');
        }
      } else {
        response = await axios.put(`${config.BASE_URL}/api/admin/contractor/${contentId}`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.status === 200) {
          setSuccessMessage('Contractor content updated successfully!');
          const saved = response.data.data;
          setExistingHeroFileUrl(saved?.hero?.fileUrl || '');
        }
      }

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving contractor data:', err);
      let errorMessage = 'Failed to save contractor data';
      if (err.response?.data?.message) errorMessage = err.response.data.message;
      else if (err.response?.status === 400) errorMessage = 'Invalid data provided';
      else if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
        setTimeout(() => navigate('/admin/login'), 2000);
      }
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="container">
          <div className="main-section">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="text-white">Loading contractor page data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="container">
        <h1 className="dash_title">Contractor Page</h1>
        <div className="main-section">
          <div className="back_btn_wrap">
            <button className="back-btn" onClick={() => navigate(-1)}>Go Back</button>
          </div>
          <div className="main-inner add_project_main">
            <div className="language-tabs" style={{ marginBottom: '20px' }}>
              <button
                type="button"
                onClick={() => setActiveLanguage('en')}
                style={{
                  padding: '10px 20px',
                  marginRight: '10px',
                  backgroundColor: activeLanguage === 'en' ? '#007bff' : '#f8f9fa',
                  color: activeLanguage === 'en' ? 'white' : 'black',
                  border: '1px solid #ddd',
                  cursor: 'pointer',
                  borderRadius: '4px',
                }}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setActiveLanguage('es')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: activeLanguage === 'es' ? '#007bff' : '#f8f9fa',
                  color: activeLanguage === 'es' ? 'white' : 'black',
                  border: '1px solid #ddd',
                  cursor: 'pointer',
                  borderRadius: '4px',
                }}
              >
                Spanish
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {successMessage && (
                <div
                  style={{
                    padding: '12px',
                    marginBottom: '20px',
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    borderRadius: '4px',
                    color: '#155724',
                  }}
                >
                  {successMessage}
                </div>
              )}

              {/* English Content */}
              {activeLanguage === 'en' && (
                <div>
                  <h3 style={{ color: '#fff', marginBottom: '20px' }}>English Content</h3>

                  <div className="section-group" style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <h4 style={{ color: '#ffd700', marginBottom: '15px', fontSize: '18px' }}>Hero Banner</h4>
                    <div className="add_field titleField">
                      <h2>Title *</h2>
                      <input
                        type="text"
                        value={formData.hero.title}
                        onChange={(e) => handleHeroChange('title', e.target.value)}
                        placeholder="Enter hero title"
                        required
                        disabled={isSubmitting}
                      />
                      {errors['hero.title'] && <p className="error-text">{errors['hero.title']}</p>}
                    </div>
                    <div className="add_field titleField">
                      <h2>Subtitle *</h2>
                      <input
                        type="text"
                        value={formData.hero.subtitle}
                        onChange={(e) => handleHeroChange('subtitle', e.target.value)}
                        placeholder="Enter hero subtitle"
                        required
                        disabled={isSubmitting}
                      />
                      {errors['hero.subtitle'] && <p className="error-text">{errors['hero.subtitle']}</p>}
                    </div>
                    <div>
                      <h2>Paragraphs</h2>
                      {formData.hero.paragraphs.map((para, index) => (
                        <div key={index} style={{ marginBottom: '10px' }} className="add_field titleField">
                          <textarea
                            value={para}
                            onChange={(e) => handleHeroParagraphChange(index, e.target.value)}
                            rows="3"
                            placeholder={`Hero paragraph ${index + 1}`}
                            disabled={isSubmitting}
                          />
                          {formData.hero.paragraphs.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeHeroParagraph(index)}
                              style={{
                                marginTop: '5px',
                                padding: '5px 15px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '4px',
                              }}
                              disabled={isSubmitting}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addHeroParagraph}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '4px',
                        }}
                        disabled={isSubmitting}
                      >
                        + Add Paragraph
                      </button>
                    </div>
                    <div className="add_field titleField">
                      <h2>Video/Image File </h2>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        onChange={(e) => setHeroFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                        disabled={isSubmitting}
                      />
                      {(heroFile || existingHeroFileUrl) && (
                        <div style={{ marginTop: '10px' }} className='wid-150'>
                         
                          {(heroFile || existingHeroFileUrl) && (
                            <div style={{ marginTop: '10px' }} className='wid-150'>
                              {(heroFile?.type?.startsWith('video/') || (existingHeroFileUrl && existingHeroFileUrl.match(/\.(mp4|webm|ogg)$/i))) ? (
                                <video
                                  controls
                                  src={heroFile ? URL.createObjectURL(heroFile) : `${config.BASE_URL}/${existingHeroFileUrl}`}
                                  style={{ maxWidth: '100%', height: 'auto' }}
                                />
                              ) : (
                                <img
                                  src={heroFile ? URL.createObjectURL(heroFile) : `${config.BASE_URL}/${existingHeroFileUrl}`}
                                  alt="Hero"
                                  style={{ maxWidth: '100%', height: 'auto' }}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="section-group" style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <h4 style={{ color: '#ffd700', marginBottom: '15px', fontSize: '18px' }}>Statistics</h4>
                    <div className="add_field titleField">
                      <h2>LED Strips Number {existingHeroFileUrl}</h2>
                      <input
                        type="text"
                        value={formData.statistics.ledStrips.number}
                        onChange={(e) => handleStatisticsChange('ledStrips', 'number', e.target.value)}
                        placeholder="e.g., 10,000+"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="add_field titleField">
                      <h2>LED Strips Description</h2>
                      <input
                        type="text"
                        value={formData.statistics.ledStrips.description}
                        onChange={(e) => handleStatisticsChange('ledStrips', 'description', e.target.value)}
                        placeholder="Enter LED strips description"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="add_field titleField">
                      <h2>Retail Space Number</h2>
                      <input
                        type="text"
                        value={formData.statistics.retailSpace.number}
                        onChange={(e) => handleStatisticsChange('retailSpace', 'number', e.target.value)}
                        placeholder="e.g., 50,000 sq ft"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="add_field titleField">
                      <h2>Retail Space Description</h2>
                      <input
                        type="text"
                        value={formData.statistics.retailSpace.description}
                        onChange={(e) => handleStatisticsChange('retailSpace', 'description', e.target.value)}
                        placeholder="Enter retail space description"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeLanguage === 'es' && (
                <div>
                  <h3 style={{ color: '#fff', marginBottom: '20px' }}>Spanish Content (Contenido en Español)</h3>

                  <div className="section-group" style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <h4 style={{ color: '#ffd700', marginBottom: '15px', fontSize: '18px' }}>Banner Principal</h4>
                    <div className="add_field titleField">
                      <h2>Título *</h2>
                      <input
                        type="text"
                        value={spanishData.hero.title}
                        onChange={(e) => handleSpanishHeroChange('title', e.target.value)}
                        placeholder="Ingrese el título del hero"
                        required
                        disabled={isSubmitting}
                      />
                      {errors['hero.title'] && <p className="error-text">{errors['hero.title']}</p>}
                    </div>
                    <div className="add_field titleField">
                      <h2>Subtítulo *</h2>
                      <input
                        type="text"
                        value={spanishData.hero.subtitle}
                        onChange={(e) => handleSpanishHeroChange('subtitle', e.target.value)}
                        placeholder="Ingrese el subtítulo del hero"
                        required
                        disabled={isSubmitting}
                      />
                      {errors['hero.subtitle'] && <p className="error-text">{errors['hero.subtitle']}</p>}
                    </div>
                    <div>
                      <h2>Párrafos</h2>
                      {spanishData.hero.paragraphs.map((para, index) => (
                        <div key={index} style={{ marginBottom: '10px' }} className="add_field titleField">
                          <textarea
                            value={para}
                            onChange={(e) => handleSpanishHeroParagraphChange(index, e.target.value)}
                            rows="3"
                            placeholder={`Párrafo ${index + 1}`}
                            disabled={isSubmitting}
                          />
                          {spanishData.hero.paragraphs.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeSpanishHeroParagraph(index)}
                              style={{
                                marginTop: '5px',
                                padding: '5px 15px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '4px',
                              }}
                              disabled={isSubmitting}
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addSpanishHeroParagraph}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '4px',
                        }}
                        disabled={isSubmitting}
                      >
                        + Agregar Párrafo
                      </button>
                    </div>
                  </div>

                  <div className="section-group" style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <h4 style={{ color: '#ffd700', marginBottom: '15px', fontSize: '18px' }}>Estadísticas</h4>
                    <div className="add_field titleField">
                      <h2>Número de Tiras LED</h2>
                      <input
                        type="text"
                        value={spanishData.statistics.ledStrips.number}
                        onChange={(e) => handleSpanishStatisticsChange('ledStrips', 'number', e.target.value)}
                        placeholder="ej., 10,000+"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="add_field titleField">
                      <h2>Descripción de Tiras LED</h2>
                      <input
                        type="text"
                        value={spanishData.statistics.ledStrips.description}
                        onChange={(e) => handleSpanishStatisticsChange('ledStrips', 'description', e.target.value)}
                        placeholder="Ingrese la descripción de las tiras LED"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="add_field titleField">
                      <h2>Número de Espacio Comercial</h2>
                      <input
                        type="text"
                        value={spanishData.statistics.retailSpace.number}
                        onChange={(e) => handleSpanishStatisticsChange('retailSpace', 'number', e.target.value)}
                        placeholder="ej., 50,000 pies cuadrados"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="add_field titleField">
                      <h2>Descripción de Espacio Comercial</h2>
                      <input
                        type="text"
                        value={spanishData.statistics.retailSpace.description}
                        onChange={(e) => handleSpanishStatisticsChange('retailSpace', 'description', e.target.value)}
                        placeholder="Ingrese la descripción del espacio comercial"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="add_project" style={{ marginTop: '30px' }}>
                <button type="submit" disabled={isSubmitting} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Save className="w-5 h-5" />
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>

            {errors.submit && (
              <div
                style={{
                  padding: '12px',
                  marginTop: '20px',
                  backgroundColor: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: '4px',
                  color: '#721c24',
                }}
              >
                {errors.submit}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorPageManage;











